import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Briefcase, Check, Home, Users, type LucideIcon } from 'lucide-react';
import { APP_CONFIG } from '../config/app';

interface SecondaryLink {
  label: string;
  to: string;
}

interface AudienceColumnProps {
  icon: LucideIcon;
  title: string;
  intro: string;
  items: readonly string[];
  ctaLabel: string;
  ctaTo: string;
  secondaryLinks?: readonly SecondaryLink[];
  delay?: number;
}

const AudienceColumn: React.FC<AudienceColumnProps> = ({
  icon: Icon,
  title,
  intro,
  items,
  ctaLabel,
  ctaTo,
  secondaryLinks = [],
  delay = 0,
}) => (
  <motion.div
    className="card flex h-full flex-col p-6 md:p-8"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.45, delay, ease: 'easeOut' }}
  >
    <div className="mb-4 flex items-center gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line bg-surface-2 text-accent-soft">
        <Icon size={20} aria-hidden />
      </span>
      <h3 className="text-xl font-extrabold tracking-[-0.01em] text-fg">{title}</h3>
    </div>
    <p className="mb-5 text-[15px] leading-[1.6] text-fg-muted">{intro}</p>
    <ul className="mb-8 space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-fg">
          <Check size={18} className="mt-0.5 shrink-0 text-accent-soft" aria-hidden />
          <span className="text-[15px] leading-snug">{item}</span>
        </li>
      ))}
    </ul>
    <div className="mt-auto flex flex-col gap-3">
      <Link to={ctaTo} className="btn-primary w-full sm:w-auto sm:self-start">
        {ctaLabel}
        <ArrowRight size={16} aria-hidden />
      </Link>
      {/* Colonnes côte à côte : même hauteur réservée sous le CTA, pour que les deux boutons soient alignés */}
      {secondaryLinks.length === 0 && <div className="hidden min-h-11 md:block" aria-hidden />}
      {secondaryLinks.length > 0 && (
        <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:gap-x-6">
          {secondaryLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="inline-flex min-h-11 items-center text-sm text-fg-muted underline-offset-4 transition-colors hover:text-accent-soft hover:underline"
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  </motion.div>
);

/** Aiguillage par profil : particuliers ou professionnels. */
const AudienceSplit: React.FC = () => (
  <section id="profils" className="section-spacing border-t border-line bg-canvas">
    <div className="container-site">
      <div className="section-title-spacing text-center">
        <div className="section-subtitle-badge">
          <Users size={14} aria-hidden />
          <span>Pour qui&nbsp;?</span>
        </div>
        <h2 className="heading-section">
          Particuliers et <span className="text-accent-soft">professionnels</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
        <AudienceColumn
          icon={Home}
          title="Particuliers"
          intro="Chez vous, à votre rythme, avec des explications claires."
          items={[
            'Réparation de téléphone',
            'Dépannage PC & Mac',
            'Récupération de données',
            'Aide à l’installation et à la prise en main',
          ]}
          ctaLabel="Je suis un particulier"
          ctaTo={`${APP_CONFIG.routes.depannage}#particuliers`}
        />
        <AudienceColumn
          icon={Briefcase}
          title="Professionnels"
          intro="Artisans, indépendants, TPE/PME : un interlocuteur unique pour votre informatique et votre site."
          items={[
            'Site web vitrine (350 € + 80 €/mois)',
            'Informatique TPE/PME (postes, réseau, maintenance)',
            'Imprimantes, NAS et périphériques réseau',
          ]}
          ctaLabel="Je suis un professionnel"
          ctaTo={`${APP_CONFIG.routes.depannage}#tpe-pme`}
          secondaryLinks={[
            { label: 'Découvrir l’offre site web', to: APP_CONFIG.routes.creationSite },
          ]}
          delay={0.08}
        />
      </div>
    </div>
  </section>
);

export default AudienceSplit;
